import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ArrayHelper, StringHelper } from "@osapp/helpers";
import { IContact } from "@osapp/model";
import { EPrestationStatus } from "@osapp/modules/prestation/models/eprestation-status.enum";
import { PrestationService } from "@osapp/modules/prestation/services/prestation.service";
import { PlatformService, Store } from "@osapp/services";
import { EStatusSeance } from "apps/idl/src/model/EStatusSeance";
import { ETraitementState } from "apps/idl/src/model/ETraitementState";
import { ITraitement } from "apps/idl/src/model/ITraitement";
import { Traitement } from "apps/idl/src/model/Traitement";
import { ActesService } from "apps/idl/src/modules/actes/actes.service";
import { FacturationService } from "apps/idl/src/modules/facturation/facturation.service";
import { EInvoiceStatus } from "apps/idl/src/modules/facturation/models/einvoice-status";
import { EModeSecurisation } from "apps/idl/src/modules/facturation/models/emode-securisation.enum";
import { IdlPrestation } from "apps/idl/src/modules/facturation/models/idl-prestation";
import { Invoice } from "apps/idl/src/modules/facturation/models/invoice";
import { ITerminalInfo } from "apps/idl/src/modules/olaqin/models/iterminal-info";
import { IAccordPrealable } from "apps/idl/src/modules/patients/model/IAccordPrealable";
import { IPatient } from "apps/idl/src/modules/patients/model/IPatient";
import { AMCP } from "apps/idl/src/modules/patients/model/amc-p";
import { AMOP } from "apps/idl/src/modules/patients/model/amo-p";
import { EUpdateMode } from "apps/idl/src/modules/patients/model/eupdate-mode.enum";
import { IAMC } from "apps/idl/src/modules/patients/model/iamc";
import { IAMO } from "apps/idl/src/modules/patients/model/iamo";
import { CouverturesService } from "apps/idl/src/modules/patients/services/couvertures.service";
import { AccordPrealableService } from "apps/idl/src/modules/traitement/slides-traitement/accordPrealable/accordPrealable.service";
import { TraitementService } from "apps/idl/src/services/traitement.service";
import { EMPTY, from, Observable, of, Subject } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap, toArray } from "rxjs/operators";
import { StoredSeance } from "../../../models/StoredSeance";
import { EHomeItems } from "../enums/EHomeItems";
import { LoaderService } from "./loader.service";
import { NavigationService } from "./navigation.service";
import { SeanceService } from "./seance.service";
import { SeancesGeneratorService } from "./seances-generator.service";
import { SnackbarService } from "./snackbar.service";


@Injectable({
	providedIn: "root"
})
export class FacturationSVService {

	constructor(
		private svcActes: ActesService,
		private svcAccordPrealable: AccordPrealableService,
		private svcSnackbar: SnackbarService,
		private svcSeanceGenerator: SeancesGeneratorService,
		private svcTraitement: TraitementService,
		private svcFacturation: FacturationService,
		private svcPrestation: PrestationService,
		private svcPlatform: PlatformService,
		private svcStore: Store,
		private svcSeance: SeanceService,
		private svcLoader: LoaderService,
		private svcNavigation: NavigationService,
		private svcCouvertures: CouverturesService
	) { }

	//Permet d'indiquer quand on a terminé une facture pour passer a la suivante
	public invoiceProcessed = new Subject<{ success: boolean, invoice: Invoice }>();
	public invoiceProcessed$ = this.invoiceProcessed.asObservable();

	triggerInvoiceProcessed(invoice: Invoice, success: boolean): void {
		this.invoiceProcessed.next({ success, invoice });
	}

	private validateAndBillPrestations(ordonnance: ITraitement, seances: StoredSeance[]): Observable<IdlPrestation[]> {
		if (!ordonnance) {
			throw new Error("Le traitement a été supprimé")
		}

		return this.svcActes.hasAccordPrealable(ordonnance.actes).pipe(
			mergeMap((hasAccordPrealable: boolean) => {
				if (!hasAccordPrealable) {
					return this.generatePrestationFromSeance(ordonnance, seances);
				} else {
					return this.svcAccordPrealable.get(ordonnance._id).pipe(
						switchMap((accord: IAccordPrealable) => {
							// Vérification de l'accord préalable
							if (!accord.dateResultat && !accord.resultat) {
								throw new Error("L'accord préalable en lien avec le traitement est incomplet.")
							} else {
								// Accord préalable complet, génération des prestations
								return this.generatePrestationFromSeance(ordonnance, seances);
							}
						}),
						catchError((error: Error) => {
							throw new Error("Erreur lors de la récupération de l'accord préalable.")
						})
					);
				}
			})
		);
	}

	private generatePrestationFromSeance(ordonnance: ITraitement, seances: StoredSeance[]): Observable<IdlPrestation[]> {
		if (!ArrayHelper.hasElements(seances)) {
			return of([]);  // Retourne un tableau vide si aucune séance n'est disponible
		}

		const traitement: Traitement = Traitement.createFromData(ordonnance);
		//séances à facturer sur la plage donnée
		const seancesValidees: StoredSeance[] = seances.filter(x => x.status === EStatusSeance.done);
		//séances restantes à faire dans tout le traitement
		const seancesAFaire: StoredSeance[] = seances.filter(seance => seance.status === EStatusSeance.to_be_done);
		//On regarde s'il reste des séances à facturer dans tout le traitement apres celle des séances que l'on va facturer
		const seancesRestantes: StoredSeance[] = seances.filter(seance =>
			!seancesValidees.some(seanceValidee => seanceValidee._id === seance._id) &&
			seance.status !== EStatusSeance.canceled &&
			seance.status !== EStatusSeance.inProgress &&
			seance.status !== EStatusSeance.completed
		);

		let prestationsToSave: IdlPrestation[] = [];

		// Création des prestations pour toutes les séances
		return from(seancesValidees).pipe(
			mergeMap((seance) =>
				this.svcSeanceGenerator.createAndSavePrestationOptimise(seance, traitement, prestationsToSave)
			),
			mergeMap((presta: IdlPrestation) =>
				seancesRestantes.length > 0 || seancesAFaire.length > 0
					? of(presta)
					: this.svcTraitement
						.modifierStateTraitement(traitement, ETraitementState.termine)
						.pipe(map(() => presta))
			),
			toArray(),
			catchError((error) => {
				return of([] as IdlPrestation[]);
			})
		);
	}

	private processFacturationStep(etape: "tarification" | "facturation" | "securisation", patient: IPatient, invoice: Invoice, seances: StoredSeance[], terminal?: ITerminalInfo): Observable<Invoice> {
		const couvAmo = patient.AMO?.find(x => this.svcCouvertures.isEligibleFacturationFromSeances(x, seances));

		return from(this.svcFacturation.forwardActionAnakin(
			patient._id,
			invoice,
			this.arePatientAndAMOUpToDate(patient, couvAmo)
		)).pipe(
			switchMap((reponseInvoice: Invoice) => {
				if (!reponseInvoice) {
					// Si reponseInvoice est null ou undefined, obtenir l'Invoice via getInvoice
					return this.svcFacturation.getInvoice(invoice._id);
				} else {
					// Sinon, utiliser reponseInvoice directement
					return of(reponseInvoice);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				this.svcFacturation.annulerFacture(patient._id, invoice, seances, true)
					.catch(error => {
						console.error("Erreur lors du rollback:", error);
					});
				throw error;
			})
		);
	}

	public async callFacturationApi(invoice: Invoice, modeSecurisation: EModeSecurisation = EModeSecurisation.SECURISE): Promise<Invoice[]> {
		if (!invoice || !invoice.patientId) return null;

		return this.svcFacturation.billPatientInvoicesAnakin(invoice.patientId, invoice, modeSecurisation);
	}

	public async callSecurisationApi(invoice: Invoice, modeSecurisation: EModeSecurisation = EModeSecurisation.SECURISE, terminal: ITerminalInfo): Promise<Invoice[]> {
		if (!invoice || !invoice.patientId) return null;

		return this.svcFacturation.secureInvoicesAnakin([invoice], modeSecurisation, terminal.terminalId,null,terminal.type);
	}

	public async callConsulterFactureApi(invoice: Invoice, numFiness: string) {
		if (!invoice || StringHelper.isBlank(numFiness)) return null;

		return this.svcFacturation.consulterFactureApi(invoice, numFiness);
	}


	public arePatientAndAMOUpToDate(patient: IPatient, AMOP: AMOP): boolean {
		if (!AMOP)
			return false;

		return !StringHelper.isBlank(patient.externalId) && AMOP.updateMode !== EUpdateMode.manual;
	}

	private verifierErreurSV(invoice: Invoice, patient: IPatient, seances: StoredSeance[]): Observable<Invoice | never> {
		if (!invoice) return EMPTY;

		if (invoice.status.value === EInvoiceStatus.priced && ArrayHelper.hasElements(invoice.erreurSv)) {
			this.svcFacturation.annulerFacture(patient._id, invoice, seances, true)
				.catch(error => {
					console.error("Erreur lors du rollback:", error);
				});
			throw new Error(invoice.erreurSv[0].libelle)
		}
		// Continue le flux avec la facture
		return of(invoice);
	}

	public preparerFacture(
		ordonnance: ITraitement,
		seances: StoredSeance[],
		patient: IPatient,
		couvertureAMO: IAMO,
		couvertureAMC: IAMC,
		couvertureAMCPatient: AMCP,
		titulaireSelected?: IContact
	) {

		interface InvoiceFlowContext {
			invoice: Invoice;
			prestations: IdlPrestation[];
		}
		this.svcLoader.showLoader("Préparation de la facture...");
		return this.validateAndBillPrestations(ordonnance, seances).pipe(
			map((prestations: IdlPrestation[]) => {
				const invoice = this.svcFacturation.createInvoiceFromPrestations(
					patient,
					prestations,
					ordonnance,
					couvertureAMO,
					couvertureAMC,
					couvertureAMCPatient,
					titulaireSelected
				);
				invoice.securisationMode = EModeSecurisation.SECURISE;
				invoice.preparationDate = new Date();

				const amc: AMCP = patient.AMC?.find(x => this.svcCouvertures.isEligibleFacturationFromPrestations(x, prestations));
				this.svcFacturation.fillInvoiceSTSAndGU(invoice, amc);

				return { invoice, prestations } as InvoiceFlowContext;
			}),
			switchMap((context: InvoiceFlowContext) =>
				this.svcFacturation.saveInvoices([context.invoice]).pipe(
					map((invoices) => ({
						...context,
						invoice: ArrayHelper.getFirstElement(invoices)
					}))
				)
			),
			switchMap((context: InvoiceFlowContext) => {
				context.prestations.forEach(p => p.status = EPrestationStatus.sent);
				return this.svcPrestation.savePrestations(context.prestations).pipe(
					map((saved) => ({
						...context,
						prestations: saved
					}))
				);
			}),
			switchMap((context: InvoiceFlowContext) =>
				this.svcPlatform.isMobileApp
					? this.svcStore.replicateSingleDocument(context.invoice._id).pipe(map(() => context))
					: of(context)
			),
			switchMap((context: InvoiceFlowContext) =>
				this.processFacturationStep("tarification", patient, context.invoice, seances).pipe(
					map((invoiceTarifiee: Invoice) => ({
						...context,
						invoice: invoiceTarifiee
					})
					)
				)
			),
			switchMap((context: InvoiceFlowContext) =>
				this.verifierErreurSV(context.invoice, patient, seances).pipe(
					map(() => context)
				)
			),
			switchMap((context: InvoiceFlowContext) =>
				this.svcPlatform.isMobileApp
					? this.svcFacturation.saveInvoices([context.invoice]).pipe(
						switchMap(() => this.svcStore.replicateSingleDocument(context.invoice._id)),
						map(() => context)
					)
					: of(context)
			),
			mergeMap((context: InvoiceFlowContext) => {
				// On récupère toutes les séances "done" et on les prépare pour l'update
				const seancesToUpdate = seances.filter(seance => seance.status === EStatusSeance.done)
					.map(seance => {
						seance.status = EStatusSeance.completed;
						return seance;
					});

				// On fait un seul appel pour tout mettre à jour
				return seancesToUpdate.length > 0
					? this.svcSeance.updateSeances(seancesToUpdate).pipe(map(() => context))
					: of(context);
			}),
			mergeMap(() => this.updateFacturesPrepareesBadgeCount()),
			tap(() => {
				this.svcLoader.hideLoader();
				this.svcSnackbar.showToast(
					"success",
					"bottom center",
					"La facture est préparée. Elle est disponible dans le suivi des factures",
					null,
					null,
					null,
					"8000"
				);
			})
		);
	}

	public updateFacturesPrepareesBadgeCount(): Observable<Invoice[]> {
		return this.svcFacturation.getInvoiceByStatus(EInvoiceStatus.priced).pipe(
			tap((invoices: Invoice[]) => {
				this.svcNavigation.setBadgeCount(EHomeItems.SuiviFacturation.label, invoices.length);
			})
		)
	}
}